import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, ArrowUp, ArrowDown } from 'lucide-react';

// Sortable row inside the selected list. Uses dnd-kit + manual move buttons
// so reorder works on touch and mouse, and is preserved in the parent state.
function SortableRow({ id, text, index, total, onRemove, onMoveUp, onMoveDown, accent }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const accentCls = accent === 'inclusion'
    ? 'border-emerald-200 bg-emerald-50/70'
    : 'border-rose-200 bg-rose-50/70';
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`selected-${accent}-${index}`}
      className={`flex items-center gap-2 p-2 rounded-lg border ${accentCls}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 px-1"
        title="Drag to reorder"
        data-testid={`drag-${accent}-${index}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="text-xs text-slate-400 font-mono w-6 shrink-0">{index + 1}.</span>
      <span className="flex-1 text-sm text-slate-700 break-words">{text}</span>
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          disabled={index === 0}
          onClick={onMoveUp}
          className="h-7 w-7 flex items-center justify-center rounded text-slate-500 hover:bg-white disabled:opacity-30"
          title="Move up"
          data-testid={`move-up-${accent}-${index}`}
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          onClick={onMoveDown}
          className="h-7 w-7 flex items-center justify-center rounded text-slate-500 hover:bg-white disabled:opacity-30"
          title="Move down"
          data-testid={`move-down-${accent}-${index}`}
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="h-7 w-7 flex items-center justify-center rounded text-slate-500 hover:bg-rose-100 hover:text-rose-600"
          title="Remove"
          data-testid={`remove-${accent}-${index}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function SortableSelectedList({ items, accent, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // dnd-kit needs unique stable IDs. Use index-prefixed text key so dupes are
  // still distinct, but since list is short (<50) this is acceptable.
  const ids = items.map((t, i) => `${i}__${t}`);

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(items, oldIdx, newIdx));
  };

  if (items.length === 0) {
    return (
      <p className="text-xs text-slate-400 italic py-3 text-center">
        Tick items above to add them here. Drag the handle to reorder — the order is preserved in the PDF.
      </p>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5">
          {items.map((text, idx) => (
            <SortableRow
              key={ids[idx]}
              id={ids[idx]}
              text={text}
              index={idx}
              total={items.length}
              accent={accent}
              onRemove={() => onChange(items.filter((_, i) => i !== idx))}
              onMoveUp={() => onChange(arrayMove(items, idx, idx - 1))}
              onMoveDown={() => onChange(arrayMove(items, idx, idx + 1))}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
